import React, { forwardRef, useImperativeHandle, useRef, useState } from  "react";
import { RCCGrid,  RCCHookToast, RCCInput,RCCRefModalProps, RCCServicesApi, RCCSwitch } from "react-common-components";
import { useForm } from "react-hook-form";
import Audit from "src/components/Audit";
import DeleteModal from "src/components/DeleteModal";
import Modal from "src/components/Modal";
import Tabs from "src/components/Tabs";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { IPermissions } from "src/context/Auth/interface";
import ModalSide from "src/components/Modal/styles";
import { IComponentContractItemsModals, IEftba016, IRefContractItemsModals } from "./interface";
import { addAndChangeSchema, deleteSchema } from "./schemas";
import HierarchyContractItems from "./components/Hierarchy";
import { IRefHierarchyComponent } from "./components/Hierarchy/interface";

const Modals: React.ForwardRefRenderFunction<IRefContractItemsModals, IComponentContractItemsModals> = ({
	paginationRef
}, ref) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();

	const modalDelete = useRef<RCCRefModalProps>(null);

	const { 
		handleSubmit, 
		register, 
		control,
		reset, 
		setError, 
		formState: { errors }, 
		clearErrors
	} = useForm();

	const { 
		handleSubmit : handleSubmitDelete, 
		register : registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();

	const [isAdd, setIsAdd] = useState(false);
	const [selectedContractItem, setSelectedContractItem] = useState<IEftba016>({} as IEftba016);
	const [permissionsData , setPermissionsData] = useState<IPermissions[]>([]);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const hierarchyContractItemsRef = useRef<IRefHierarchyComponent>(null);

	const submitAddOrChange = async (data: any) => {
		let permissionsDt = Object.keys(data).filter((key) => !key.includes("eftba016") && !key.includes("remove_")).map((key) => data[key]);
		permissionsDt = permissionsDt.map((pData) => ({
			...pData,
			id: pData.id ? pData.id : null,
			contractHasAccess: pData.contractHasAccess
		}));
		
		try{
			clearErrors();
			modalAddOrChange.current?.setLoadingButton();
			
			await addAndChangeSchema(translate).validate({...data}, {
				abortEarly: false
			});

			const dataEftba016Changed = data.eftba016;
			dataEftba016Changed.efa016Ativo = data.eftba016.efa016Ativo ? "S" : "N";
			dataEftba016Changed.efa016Codigo = isAdd ? data.eftba016.efa016Codigo : selectedContractItem.efa016Codigo;

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "ContractItems/Insert",
					body: {
						eftba016 : data.eftba016,
						permissions: permissionsDt
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "ContractItems/Update",
					body: {
						eftba016 : data.eftba016,
						permissions: permissionsDt
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Item de contrato adicionado com sucesso") : translate("Item de contrato alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err: any){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setError))
				return;

			const textMessageError = err?.response?.data?.Message;
			if(textMessageError === "codeInUse"){
				addToast({
					title: translate("Erro"),
					description: translate("Este código já está cadastrado"),
					color: "red"
				});
			}else{
				standardErrorMessages(err, isAdd ? translate("Erro ao adicionar item de contrato") : translate("Erro ao alterar item de contrato"), addToast);
			}
		
		}
	};

	const submitDelete = async (data: any) => {
		try{	
			clearErrorsDelete();
			modalDelete.current?.setLoadingButton();

			await deleteSchema(translate).validate({...data, "efa016DescricaoConfirmation": selectedContractItem.efa016Descricao},{
				abortEarly: false
			});

			await RCCServicesApi.deleteRequest({
				method: "ContractItems/Delete",
				urlParameters: {
					code: selectedContractItem.efa016Codigo
				}
			});

			addToast({
				color: "green",
				description: translate("Item de contrato excluído com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err: any){

			modalDelete.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, translate("Erro ao excluir item de contrato"), addToast);
		}	
	};

	const queryPermissions = async () => {
		setLoading(true);
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "ContractItems/ListPermissions"
			});
			
			setPermissionsData(data);
			setLoading(false);
			modalAddOrChange.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
		}
	};

	const queryContractItem = async (code: any) => {
		setLoading(true);
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "ContractItems/Select",
				urlParameters: {
					code
				}
			});
			
			setSelectedContractItem(data.eftba016);
			setPermissionsData(data.permissionsDataDTO);
			setLoading(false);
			modalAddOrChange.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
		}
	};

	const actionButtonAdd = async () => {
		reset();

		setIsAdd(true);
		setSelectedContractItem({} as IEftba016);
		await queryPermissions();
		hierarchyContractItemsRef.current?.reset();
		hierarchyContractItemsRef.current?.compress();
	};

	const actionButtonChange = (row: IEftba016) => {
		reset();
		setPermissionsData([]);
		setIsAdd(false);
		hierarchyContractItemsRef.current?.reset();
		hierarchyContractItemsRef.current?.compress();
		queryContractItem(row.efa016Codigo);
	};

	const actionButtonDelete = (row: IEftba016) => {
		resetDelete(); 
		setSelectedContractItem(row); 
		modalDelete.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	return (
		<>
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão de item de contrato")}
				description= {`${translate("Deseja realmente excluir o item de contrato")}: ${selectedContractItem.efa016Descricao}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="efa016Descricao"
						label={translate("Digite a descrição do item de contrato para excluí-lo")}
						maxLength={200}
						error={errorsDelete.efa016Descricao}
						required
					/>
				</RCCGrid>
			</DeleteModal>
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar Item de Contrato") : translate("Alterar Item de Contrato"),
					subtitle: !isAdd ? `${translate("Item de contrato")}: ${selectedContractItem.efa016Descricao}`: "",
					icon: {
						name: isAdd ? "plus" : "pencil"
					}
				}}
				styleInline={ModalSide}
				onSubmit={handleSubmit(submitAddOrChange)}
			>

				<Tabs 
					name="tabsContractItems"
					tabInit={0}
					options={[
						{
							label: translate("Geral"),
							content: (
								<RCCGrid
									container
									item
									direction="column"
									alignItems="center"
								>
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
										alignItems="center"
										justify="flex-end"
									>
										<Audit data={selectedContractItem} type="pop"/>
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
									>
										<RCCInput 
											register={register}
											name="eftba016.efa016Codigo"
											label={translate("Código")}
											maxLength={20}
											defaultValue={selectedContractItem.efa016Codigo ?? ""}
											error={errors.eftba016?.efa016Codigo}
											disabled={!isAdd}
											required
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
									>
										<RCCInput 
											register={register}
											name="eftba016.efa016Descricao"
											label={translate("Descrição")}
											maxLength={60}
											defaultValue={selectedContractItem.efa016Descricao ?? ""}
											error={errors.eftba016?.efa016Descricao}
											required
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
										style={{marginTop: "10px"}}
									>
										<RCCSwitch 
											label={translate("Ativo")} 
											name="eftba016.efa016Ativo" 
											defaultChecked={selectedContractItem.efa016Ativo === "S" || isAdd} 
											error={errors.eftba016?.efa016Ativo}
											register={register}/>
									</RCCGrid>
								</RCCGrid>
							)
						},{
							label: translate("Ações Relacionadas"),
							content: (
								<>
									<HierarchyContractItems 
										ref={hierarchyContractItemsRef}
										permissionsData={permissionsData} 
										register={register} 
										control={control}							
									/>
								</>
							)
						}
					]}
				/>
			</Modal>
		</>
	);
};


export default forwardRef(Modals);
